import React from 'react';
import { Field, Form, Formik, FormikProps } from 'formik';
import FormInput from 'shared/Inputs/Inline/FormInput';
import Button from 'shared/Button/Button';
import * as Yup from 'yup';
import { AdminDetails as Details } from 'store/admins/types';
import FullAddress from 'components/Forms/Address/Templates/FullAddress';
import './_FormStyle.scss';
import { telephoneMask } from 'shared/Inputs/inputMasks';
import { BUTTON_MODS } from 'shared/Inputs/interface';
import { telephoneValidation } from 'utils/form/validation';
import { populateAddressHandlerNumberConcat } from 'utils/address/form';

type Props = {
  error: string;
  details: Details;
  onSubmit: (values: Details) => void;
  onBack: () => void;
  isSaved: boolean;
};

const validationSchema = Yup.object().shape({
  address: Yup.object().shape({
    city: Yup.string().required('This field is required.'),
    postcode: Yup.string().required('This field is required.'),
  }),
  email: Yup.string().email('Please provide correct email address.'),
  contactNumber: telephoneValidation(),
});

type Address = {
  buildingNumber: string;
  buildingName: string;
  street: string;
  city: string;
  county: string;
  postcode: string;
};

type FormData = {
  name: string;
  fcaNumber: string;
  address: Address;
  fullAddress: string;
  email: string;
  contactNumber: string;
  edit: {
    address: boolean;
    email: boolean;
    contactNumber: boolean;
  };
};

const formatAddressInline = (address: Address) =>
  [address.buildingNumber, `${address.buildingName},`, `${address.street},`, `${address.city},`, address.postcode].join(
    ' ',
  );

const AdminDetails: React.FC<Props> = ({ error, details, onSubmit, onBack, isSaved }: Props) => {
  const formData: FormData = {
    ...details,
    name: `${details.firstName} ${details.lastName}`,
    edit: {
      address: false,
      email: false,
      contactNumber: false,
    },
    fullAddress: formatAddressInline(details.address),
  };

  const handleSubmit = (values: FormData) => {
    const submitData: Details = {
      ...details,
      email: values.email,
      address: values.address,
      contactNumber: values.contactNumber,
    };

    onSubmit(submitData);
  };

  const onContactNumberChange = (setFieldValue: any, values: any) => {
    setFieldValue('edit.contactNumber', !values.edit.contactNumber);
  };

  const onAddressChange = (setFieldValue: any, values: any) => {
    setFieldValue('edit.address', !values.edit.address);
    setFieldValue('fullAddress', formatAddressInline(values.address));
  };

  const onEmailChange = (setFieldValue: any, values: any) => {
    setFieldValue('edit.email', !values.edit.email);
  };

  return (
    <Formik
      initialValues={formData}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      enableReinitialize={true}>
      {({ values, setFieldValue, errors, touched }: FormikProps<any>) => {
        const isValid = (fieldName: string) => !!(errors && errors[fieldName] && touched && touched[fieldName]);
        const isCorrect = (fieldName: string) =>
          Boolean(!(errors && errors[fieldName]) && touched && touched[fieldName]);

        return (
          <Form className="form admin-details">
            <h2 className="admin-details__h1">Admin Details</h2>

            <Field
              name="name"
              component={FormInput}
              label="Name"
              readOnly={true}
              wrapperClass={'form__wrapper inline border bottom'}
            />

            <Field
              name="fcaNumber"
              component={FormInput}
              label="FCA No"
              readOnly={true}
              wrapperClass={'form__wrapper inline border bottom'}
            />

            <Field
              name="fullAddress"
              component={FormInput}
              label="Address"
              readOnly={true}
              error={errors && errors.address ? 'Please amend address' : ''}
              inputWidth={8}
              buttonWidth={4}
              onChangeClick={() => onAddressChange(setFieldValue, values)}
              mode={values.edit.address ? BUTTON_MODS.OK : BUTTON_MODS.CHANGE}
              wrapperClass={'form__wrapper inline border bottom'}
            />

            {values.edit.address && (
              <FullAddress
                wrapperClass="new-ui"
                useRebrand
                isValid={isValid}
                isCorrect={isCorrect}
                prefix="address"
                requiredFields={{}}
                onSelect={populateAddressHandlerNumberConcat(setFieldValue)}
                showFlatNumber={false}
              />
            )}

            <Field
              name="email"
              component={FormInput}
              label="Email Address"
              type="email"
              inputWidth={8}
              buttonWidth={4}
              onChangeClick={() => onEmailChange(setFieldValue, values)}
              mode={values.edit.email ? BUTTON_MODS.OK : BUTTON_MODS.CHANGE}
              readOnly={!values.edit.email}
              wrapperClass={'form__wrapper inline border bottom'}
            />

            <Field
              name="contactNumber"
              component={FormInput}
              label="Contact No"
              mask={telephoneMask}
              showMask={true}
              inputWidth={8}
              buttonWidth={4}
              onChangeClick={() => onContactNumberChange(setFieldValue, values)}
              mode={values.edit.contactNumber ? BUTTON_MODS.OK : BUTTON_MODS.CHANGE}
              readOnly={!values.edit.contactNumber}
              wrapperClass={'form__wrapper inline border bottom'}
            />

            <div className="form__navigation">
              <div className="form__navigation-controls">
                <Button className="form__submit" type="button" label="Back" onClick={onBack} />
                <div>
                  {isSaved && <span className="form-saved">Saved!</span>}
                  {error && !isSaved && <span className="form-failure">{error}</span>}
                  <Button className="form__submit" type="submit" label="Save" />
                </div>
              </div>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default AdminDetails;
